import axios from 'axios';
import API_MAP, { API_DOMAIN, COOKIES_REFRESH_TOKEN_KEY, WHITEBOARDS_DOMAIN } from 'constants/api';
import queryString from 'query-string';
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from 'utils/cookies';
import { directToAuth } from 'utils/functions';
import { setIsLoaded, setIsLoading } from 'utils/helpers';
// Set up default config for http requests here
// Please have a look at here `https://github.com/axios/axios#request- config` for the full list of configs

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};



function safeParse(str) {
    try {
        return JSON.parse(str);
    } catch (e) {
        return str;
    }
}

export const refreshTokenWrapper = async (inputToken, autoRedirect = true) => {
    await navigator.locks.request(`${COOKIES_REFRESH_TOKEN_KEY}-checking`, async (_)=>{
        if (isRefreshing) {
            return new Promise(function (resolve, reject) {
                failedQueue.push({ resolve, reject });
            })
                .then(token => {
                    return token;
                })
                .catch(err => {
                    return Promise.reject(err);
                });
        }
    
        isRefreshing = true;
    })

    let responseToken = null;
    await navigator.locks.request(COOKIES_REFRESH_TOKEN_KEY, async (lock) => {
        try {
            const res = await axios.post(API_MAP.AUTH.REFRESH_TOKEN, { refreshToken: getRefreshToken() || inputToken })
            const { token, refreshToken } = res.data?.detail
            setTokens(token, refreshToken)
            processQueue(null, token);
            responseToken = token;
        } catch (err) {
            processQueue(err, null);
            localStorage.removeItem('persist:root');
            clearTokens()
            directToAuth(autoRedirect)
            responseToken = null;
        } finally {
            isRefreshing = false
        };
    });
    return responseToken;

}

const responseInterceptors = async (error, originCaller) => {
    const originalRequest = error.config;
    if (error?.response?.status === 401 && !originalRequest._retry) {

        try {
            const token = await refreshTokenWrapper(getRefreshToken());
            originalRequest._retry = true;
            originCaller.defaults.headers.common['token'] = token;
            originalRequest.headers['token'] = token;
            if (originalRequest?.data) {
                originalRequest.data = safeParse(originalRequest?.data)
            }
            return originCaller(originalRequest);
        } catch (e) {
            return Promise.reject(e);
        }

    } else {
        if (error.response) {
            // eslint-disable-next-line default-case
            switch (error.response.status) {
                case 401:
                    localStorage.removeItem('persist:root');
                    clearTokens()
                    directToAuth()
                    break;
            }
        }
    }
    return Promise.reject(error);
}

const axiosClient = axios.create({
    baseURL: API_DOMAIN,
    headers: {
        'content-type': 'application/json',
    },
    paramsSerializer: (params) => queryString.stringify(params),
});

axiosClient.interceptors.request.use(async (config) => {
    const token = getAccessToken()
    setIsLoading()
    config.headers = { token: token };
    return config;
});

axiosClient.interceptors.response.use(
    (response) => {
        setIsLoaded()
        return response.data;
    },
    async (error) => await responseInterceptors(error, axiosClient),
);

const axiosFullResponse = axios.create({
    baseURL: API_DOMAIN,
    headers: {
        'content-type': 'application/json',
    },
    paramsSerializer: (params) => queryString.stringify(params),
});

axiosFullResponse.interceptors.request.use(async (config) => {
    const token = getAccessToken()
    config.headers = { token: token };
    return config;
});

axiosFullResponse.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => await responseInterceptors(error, axiosFullResponse),
);

const whiteboardClient = axios.create({
    baseURL: WHITEBOARDS_DOMAIN,
    headers: {
        'content-type': 'application/json',
    },
    paramsSerializer: (params) => queryString.stringify(params),
});

whiteboardClient.interceptors.request.use(async (config) => {
    const token = getAccessToken()
    config.headers = { token: token };
    return config;
});

whiteboardClient.interceptors.response.use(
    (response) => {
        return response.data;
    },
    async (error) => await responseInterceptors(error, whiteboardClient),
);

export default axiosClient;
export { axiosFullResponse, whiteboardClient };
